import React, { FunctionComponent, useEffect, useRef } from 'react';

import { m as motion, useAnimation } from 'framer-motion';
import { AnimationControls } from 'framer-motion/types/animation/types';
import styled from 'styled-components';

import { Button } from 'chime-ui/components/Button';
import {
  MoneyBox,
  PadLockIcon,
  ScissorFeesIcon,
  ThreePeopleIcon,
} from 'chime-ui/components/Icons';
import {
  Superscript,
  Text,
  TextBlock,
  Title,
} from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';
import media from 'chime-ui/system/media';

import Layout, { CopyrightText, Footer } from 'components/Layout';
import { CenteredHeader } from 'components/Layout/Header';

import { useIntersect } from 'hooks/useIntersect';
import { useUserContext } from 'hooks/useUserContext';
import { useValuePropsSegmentEvents } from 'hooks/useValuePropsSegmentEvents';

import {
  contentAnimations,
  iconAnimations,
  titleAnimations,
} from '../Confirmation.animations';
import useReceiveFromAnyoneConfirmationPage from './useReceiveFromAnyoneConfirmationPage';

const ReceiveFromAnyoneConfirmation: FunctionComponent = () => {
  const containerRef = useRef(null);
  const [userData] = useUserContext();
  const { amount, name } = userData;
  const {
    onJoinChimeClick,
    trackConfirmationAnimationViewed,
  } = useReceiveFromAnyoneConfirmationPage();

  const animationControls = useAnimation();
  useEffect(() => {
    const startAnimation = async () => {
      trackConfirmationAnimationViewed();

      await animationControls.start('showTitle');
      await animationControls.start('showIcon');
      await animationControls.start('contentSlide');
    };

    startAnimation();
  }, [animationControls, trackConfirmationAnimationViewed]);

  const [setNode, entry] = useIntersect({
    root: null,
    rootMargin: '0px',
    threshold: [0.5, 0.9],
  });

  useEffect(() => {
    setNode(containerRef.current);
  }, [setNode]);

  useValuePropsSegmentEvents(entry, 'RFA Confirmation Page');

  return (
    <Layout
      header={<CenteredHeader />}
      footer={<AnimatedFooter animationControls={animationControls} />}
    >
      <MoneyIconLayout
        initial={'hidden'}
        animate={animationControls}
        variants={iconAnimations}
      >
        <MoneyBox />
      </MoneyIconLayout>
      <TitleLayout
        initial={'hidden'}
        animate={animationControls}
        variants={titleAnimations}
      >
        <Title size={'small'}>
          You paid {name} ${amount}!
        </Title>
      </TitleLayout>
      <motion.div
        initial={'hidden'}
        animate={animationControls}
        variants={contentAnimations}
      >
        <ValuePropsLayout ref={containerRef}>
          <Separator />
          <ListBoxTitleLayout>
            <Text size={'standard'} bold={true}>
              Join {name} on Chime
            </Text>
          </ListBoxTitleLayout>
          <ListBoxLayout>
            <ListBox>
              <IconLayout>
                <ThreePeopleIcon />
              </IconLayout>
              <Text>
                Send and receive money fee-free, no matter what bank account
                they use
              </Text>
            </ListBox>
            <ListBox>
              <IconLayout>
                <ScissorFeesIcon />
              </IconLayout>
              <Text>No cash-out fees for you or anyone</Text>
            </ListBox>
            <ListBox>
              <IconLayout>
                <PadLockIcon />
              </IconLayout>
              <Text>
                Your funds are FDIC-insured up to $250,000 through our bank
                partners
                <Superscript>1</Superscript>
              </Text>
            </ListBox>
          </ListBoxLayout>
        </ValuePropsLayout>
        <ButtonLayout>
          <Button onClick={onJoinChimeClick}>Join Chime</Button>
        </ButtonLayout>
      </motion.div>
    </Layout>
  );
};

function AnimatedFooter({
  animationControls,
}: {
  animationControls: AnimationControls;
}) {
  return (
    <motion.div
      initial={'hidden'}
      animate={animationControls}
      variants={contentAnimations}
    >
      <TermsAndConditionsLayout>
        <TextBlock size={'small'}>
          <Superscript>1</Superscript> Deposits in Chime accounts are FDIC
          insured through The Bancorp Bank or Stride Bank; Members FDIC.
        </TextBlock>
      </TermsAndConditionsLayout>
      <Footer>{<CopyrightText />}</Footer>
    </motion.div>
  );
}

const MoneyIconLayout = styled(motion.div)`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ButtonLayout = styled.div`
  ${media.tablet`
    margin-bottom: 2rem;
  `};
`;

const TitleLayout = styled(motion.div)`
  margin: 2rem 0;
  text-align: center;
`;

const TermsAndConditionsLayout = styled.div`
  padding-top: 1.5rem;
  text-align: left;
`;

const ValuePropsLayout = styled.div`
  margin: 2rem 0;
`;

const Separator = styled.div`
  height: 0;
  width: 112px;
  border-top: solid 1px ${colors.lightGrey};
  margin: 0 auto;
`;

const ListBoxTitleLayout = styled.div`
  text-align: center;
  margin: 2rem 0 1.5rem 0;
`;

const ListBoxLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 1.5rem;
`;

const ListBox = styled.div`
  display: grid;
  grid-column-gap: 1.5rem;
  grid-template-columns: min-content 2fr;
  align-items: center;
`;

const IconLayout = styled.div`
  height: 80px;
  width: 80px;
  grid-column-start: 1;
`;

export default ReceiveFromAnyoneConfirmation;
